import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import { can } from '@/services/acl';

import AppView from '@/views/AppView.vue';
import LoginView from '@/views/LoginView.vue';
import ForgotPasswordView from '@/views/ForgotPasswordView.vue';
import NotFoundView from '@/views/NotFoundView.vue';
import AccessDeniedView from '@/views/AccessDeniedView.vue';

import DashboardHome from '@/components/dashboard/DashboardHome.vue';
import SettingsHome from '@/components/settings/SettingsHome.vue';
import CarListings from '@/components/listing/cars/Cars.vue';

import Clients from '@/components/modules/clients/Clients.vue';
import Opportunities from '@/components/modules/opportunities/Opportunities.vue';

import Sales from '@/components/modules/sales/Sales.vue';
import Expenses from '@/components/modules/expenses/Expenses.vue';
import Collections from '@/components/modules/collections/Collections.vue';
import Influencers from '@/components/modules/influencers/Influencers.vue';
import Quotations from '@/components/modules/quotations/Quotations.vue';

import Activities from '@/components/modules/activities/Activities.vue';

import Reports from '@/components/reports/Container.vue';

import Reassignments from '@/components/settings/Reassignments.vue';
import Integrations from '@/components/settings/Integrations.vue';
import Roles from '@/components/settings/roles/Roles.vue';

import Profile from '@/components/auth/Profile.vue';
import Catalogs from '@/components/catalogs/CatalogsContainer.vue';
import Products from '@/components/listing/products/Products.vue';
import Leads from '@/components/modules/leads/Leads.vue';

import Users from '@/components/modules/users/Users.vue';

// Helper: regresa el primer hijo permitido según el orden actual de children
function nextAllowedFromChildren(children:any) {
  for (const r of children) {
    // saltar el entry '' (landing) y Opportunities (ya lo intentamos)
    if (r.path === '' || r.name === 'Opportunities') continue;

    const perm = r.meta?.permission;
    if (!perm || can(perm)) {
      // preferir navegar por name si existe
      return r.name ? { name: r.name } : { path: r.path };
    }
  }
  return { name: 'AccessDenied' };
}

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
  },
  {
    path: '/forgot-password',
    name: 'Forgot Password',
    component: ForgotPasswordView,
  },
  {
    path: '/',
    component: AppView,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Dashboard',
        redirect: () => {
          // 1) intenta Opportunities
          if (can('opportunities.access')) return { name: 'Opportunities' };
          // 2) si no, cae al primer hijo permitido según el orden actual
          return nextAllowedFromChildren(appChildren);
        },
      },
      {
        path: 'settings',
        name: 'Settings',
        component: SettingsHome,
        meta: { permission: 'settings.access' },
      },
      {
        path: 'car-listings',
        name: 'Cars',
        component: CarListings,
        meta: { permission: 'quotations.access' },
      },
      {
        path: 'clients/:id?',
        name: 'Clients',
        component: Clients,
        meta: { permission: 'clients.access' },
      },
      {
        path: 'quotations/:id?',
        name: 'Quotations',
        component: Quotations,
        meta: { permission: 'quotations.access' },
      },
      {
        path: 'sales/:id?',
        name: 'Sales',
        component: Sales,
        meta: { permission: 'sales.access' },
      },
      {
        path: 'sales/admin/:id?',
        component: Sales,
        props: { view: 'admin' },
        meta: { permission: 'sales.admin' },
      },
      {
        path: 'sales/continuity/:id?',
        component: Sales,
        props: { view: 'continuity' },
        meta: { permission: 'sales.continuity' },
      },
      {
        path: 'cancellations',
        name: 'Cancellations',
        component: Sales,
        props: { view: 'cancellations' },
        meta: { permission: 'sales.access' },
      },
      {
        path: 'expenses/:id?',
        name: 'Expenses',
        component: Expenses,
        meta: { permission: 'expenses.access' },
      },
      {
        path: 'collections/:id?',
        name: 'Collections',
        component: Collections,
        meta: { permission: 'collections.access' },
      },
      {
        path: 'influencers/:id?',
        name: 'Influencers',
        component: Influencers,
        meta: { permission: 'influencers.access' },
      },
      {
        path: 'opportunities/:id?',
        name: 'Opportunities',
        component: Opportunities,
        meta: { permission: 'opportunities.access' },
      },
      {
        path: 'reports',
        name: 'Reports',
        component: Reports,
        meta: { permission: 'reports.access' },
      },
      {
        path: 'activities/:id?',
        name: 'Activities',
        component: Activities,
        meta: { permission: 'activities.access' },
      },
      {
        path: 'reassignments',
        name: 'Reassignments',
        component: Reassignments,
        meta: { permission: 'reassignments.access' },
      },
      {
        path: 'integrations',
        name: 'Integrations',
        component: Integrations,
        meta: { permission: 'integrations.access' },
      },
      {
        path: 'roles',
        name: 'Roles',
        component: Roles,
        meta: { permission: 'roles.access' },
      },
      {
        path: 'catalogs',
        name: 'Catalogs',
        component: Catalogs,
        meta: { permission: 'catalogs.access' },
      },
      {
        path: 'products',
        name: 'Products',
        component: Products,
        meta: { permission: 'products.access' },
      },
      {
        path: 'leads',
        name: 'Leads',
        component: Leads,
        meta: { permission: 'leads.access' },
      },
      {
        path: 'profile',
        name: 'Profile',
        component: Profile,
      },
      {
        path: '/users',
        component: Users,
        //meta:{ permission: 'users.access' }
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFoundView,
  },
  {
    path: '/acceso-denegado',
    name: 'AccessDenied',
    component: AccessDeniedView,
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// referencia a los hijos ya definidos para usar en el redirect/guard
const appChildren = routes.find(r => r.path === '/')?.children as any;

// Navigation guard
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();
  const isAuthenticated = authStore.isAuthenticated;

  // requiere auth
  if (to.meta.requiresAuth && !isAuthenticated) {
    next({ name: 'Login' });
    return;
  }

  // ya autenticado e intenta ir a login -> aplica misma política que "/"
  if (to.name === 'Login' && isAuthenticated) {
    const target = can('opportunities.access')
      ? { name: 'Opportunities' }
      : nextAllowedFromChildren(appChildren);
    next(target);
    return;
  }

  // permisos por ruta
  if (typeof to.meta.permission === 'string' && !can(to.meta.permission)) {
    next({ name: 'AccessDenied' });
    return;
  }

  next();
});

export default router;
